import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { ArrowRight } from 'lucide-react'
import { ContractAttachments } from '@/components/contracts/ContractAttachments'
import { JobStatusBadge } from '@/components/shared/StatusBadges'
import { usePermissions } from '@/hooks/usePermissions'
import { cn } from '@/lib/utils'
import { formatDate } from '@/lib/format'
import {
  CONTRACT_DETAIL_INTERVENTIONS_QUERY,
  CONTRACT_DETAIL_TAB_STALE_TIME,
  useContractInterventions,
} from '@/hooks/useContracts'
import { ServiceCategoryIcon } from '@/components/icons/ServiceCategoryIcons'
import {
  JOB_STATUS,
  PLANNING_FREQUENCY_LABELS,
  type ContractBringItem,
  type ContractDetail,
  type ContractTimeSlot,
  type Documentable,
  type Job,
  type JobStatus,
  type Weekday,
} from '@/types'
import paperclipIcon from '@/assets/contracts/detail/paperclip.svg'
import checkIcon from '@/assets/contracts/detail/check.svg'
import clockHeaderIcon from '@/assets/contracts/detail/clock-header.svg'
import bugIcon from '@/assets/contracts/detail/bug.svg'
import calendarPlanningIcon from '@/assets/contracts/detail/calendar-planning.svg'
import wrenchIcon from '@/assets/contracts/detail/wrench.svg'

const cardShadow =
  'shadow-[0px_8px_16px_-4px_rgba(38,45,66,0.1),0px_1px_4px_-4px_rgba(38,45,66,0.05)]'
const cardClass = cn(
  'flex min-h-0 min-w-[240px] flex-col overflow-hidden rounded-2xl border border-[#e7e9ef] bg-white',
  cardShadow
)
const cardH340 = 'h-[340px]'
const headerLinkClass =
  'inline-flex shrink-0 items-center gap-2 rounded-lg p-2 text-xs font-medium leading-4 text-[#ff5a1f] hover:bg-[#fff1ea]'

function formatStamp(job: Job, language: string) {
  const date = formatDate(job.scheduled_date)
  if (!job.start_time) return date
  const [h, m] = job.start_time.split(':')
  if (h == null || Number.isNaN(Number(h))) return date
  const d = new Date()
  d.setHours(Number(h), Number(m ?? 0), 0, 0)
  const locale = language.startsWith('fr') ? 'fr-FR' : 'en-US'
  const time = d.toLocaleTimeString(locale, {
    hour: 'numeric',
    minute: Number(m) ? '2-digit' : undefined,
  })
  return `${date} ${time}`
}

function jobPlace(job: Job) {
  return [job.postal_code, job.city].filter(Boolean).join(' ')
}

function recentJobs(jobs: Job[], limit = 4): Job[] {
  return jobs
    .filter(
      j =>
        j.status !== JOB_STATUS.completed && j.status !== JOB_STATUS.cancelled
    )
    .slice(0, limit)
}

function MetaDot() {
  return (
    <span className='size-1 shrink-0 rounded-full bg-[#b0b5c3]' aria-hidden />
  )
}

function TimelineMarker({
  status,
  last,
}: {
  status: JobStatus
  last: boolean
}) {
  const completed = status === JOB_STATUS.completed
  return (
    <div className='relative flex h-6 w-6 shrink-0 flex-col items-center'>
      <span
        className={cn(
          'flex size-6 shrink-0 items-center justify-center rounded-full border-2 p-0.5',
          completed ? 'border-[#ecfdf5]' : 'border-[#fff1ea]'
        )}
      >
        <span
          className={cn(
            'flex size-4 items-center justify-center rounded-full',
            completed ? 'bg-[#10b981]' : 'bg-[#ff5a1f]'
          )}
        >
          {completed ? (
            <img
              src={checkIcon}
              alt=''
              width={11}
              height={8}
              className='h-[8px] w-[11px] max-w-none'
            />
          ) : (
            <span className='size-2 rounded-full bg-white' />
          )}
        </span>
      </span>
      {!last && (
        <span
          className='absolute left-1/2 top-[26px] h-9 w-px -translate-x-1/2 bg-[#e7e9ef]'
          aria-hidden
        />
      )}
    </div>
  )
}

function CardHeader({
  icon,
  iconBoxClass,
  title,
  action,
}: {
  icon: ReactNode
  iconBoxClass: string
  title: string
  action?: ReactNode
}) {
  return (
    <div className='flex h-16 items-center gap-2 px-6 pb-2 pt-6'>
      <div className='flex min-w-0 flex-1 items-center gap-3'>
        <span
          className={cn(
            'flex size-8 shrink-0 items-center justify-center overflow-hidden rounded-lg p-2',
            iconBoxClass
          )}
        >
          {icon}
        </span>
        <h2 className='font-heading truncate text-base font-semibold leading-[1.2] text-[#171a20]'>
          {title}
        </h2>
      </div>
      {action}
    </div>
  )
}

function assignedTechnicianNames(jobs: Job[]): string | null {
  const names = new Map<string, string>()
  for (const j of jobs) {
    for (const tech of j.assigned_technicians ?? []) {
      const name = tech.full_name?.trim()
      if (!name) continue
      names.set(tech.id, name)
    }
  }
  return names.size > 0 ? Array.from(names.values()).join(', ') : null
}

function formatTimeSlots(slots: ContractTimeSlot[] | undefined, empty: string) {
  if (!slots?.length) return empty
  return slots
    .map(s => `${s.start.slice(0, 5)} - ${s.end.slice(0, 5)}`)
    .join(', ')
}

const WEEKDAY_ORDER: Weekday[] = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
]

function formatPreferredDays(
  days: Weekday[] | undefined,
  locale: string,
  empty: string
) {
  if (!days?.length) return empty
  const loc = locale.startsWith('fr') ? 'fr-FR' : 'en-US'
  const monday = new Date(Date.UTC(2024, 0, 1)) // Monday
  return [...days]
    .sort((a, b) => WEEKDAY_ORDER.indexOf(a) - WEEKDAY_ORDER.indexOf(b))
    .map(d => {
      const i = WEEKDAY_ORDER.indexOf(d)
      const date = new Date(monday)
      date.setUTCDate(monday.getUTCDate() + i)
      const weekday = date.toLocaleDateString(loc, {
        weekday: 'long',
        timeZone: 'UTC',
      })
      return weekday.charAt(0).toUpperCase() + weekday.slice(1)
    })
    .join(', ')
}

// Figma 3475:169777 + 3479:38933 — Information tab.
export function ContractDetailInformationCards({
  contract,
  documents,
  documentsLoading,
  onViewAll,
  onDocumentsChanged,
  onOpenJob,
}: {
  contract: ContractDetail
  documents: Documentable[]
  documentsLoading: boolean
  onViewAll: () => void
  onDocumentsChanged: () => void
  onOpenJob: (jobId: string) => void
}) {
  const { t, i18n } = useTranslation(['contracts', 'clients'])
  const { canEdit } = usePermissions()
  const recent = useContractInterventions(
    contract.id,
    CONTRACT_DETAIL_INTERVENTIONS_QUERY,
    { staleTime: CONTRACT_DETAIL_TAB_STALE_TIME }
  )
  const items = recentJobs(recent.data?.data ?? [])
  const dash = t('detail.information.emptyValue')
  const pests = contract.service_categories ?? []
  const bringItems = contract.items_to_bring ?? []
  const technicians = assignedTechnicianNames(recent.data?.data ?? [])
  const planningRows = [
    {
      label: t('detail.information.planning.interventionsPerYear'),
      value: String(contract.interventions_per_year ?? dash),
    },
    {
      label: t('detail.information.planning.frequency'),
      value: contract.planning_frequency
        ? PLANNING_FREQUENCY_LABELS[contract.planning_frequency]
        : dash,
    },
    {
      label: t('detail.information.planning.days'),
      value: formatPreferredDays(contract.preferred_days, i18n.language, dash),
    },
    {
      label: t('detail.information.planning.niche'),
      value: formatTimeSlots(contract.preferred_time_slots, dash),
    },
    {
      label: t('detail.information.planning.leadTechnician'),
      value: technicians ?? dash,
    },
  ]

  return (
    <div className='flex flex-col gap-4'>
      <div className='flex flex-col gap-4 xl:flex-row xl:items-stretch'>
        <section className={cn(cardClass, 'flex-1 [&_h2]:text-sm')}>
          <CardHeader
            icon={
              <img
                src={clockHeaderIcon}
                alt=''
                width={16}
                height={16}
                className='size-4 max-w-none'
              />
            }
            iconBoxClass='bg-[#f4f5f8]'
            title={t('detail.recentInterventions.title')}
            action={
              <button
                type='button'
                className={headerLinkClass}
                onClick={onViewAll}
              >
                {t('detail.recentInterventions.viewAll')}
                <ArrowRight className='size-4' />
              </button>
            }
          />
          <div className='flex flex-col gap-4 px-6 pb-6 pt-4'>
            {recent.isLoading ? (
              <p className='text-sm text-[#3d434e]'>{t('detail.loading')}</p>
            ) : items.length === 0 ? (
              <p className='text-sm text-[#3d434e]'>
                {t('detail.interventions.empty')}
              </p>
            ) : (
              items.map((job, i) => {
                const tech = job.assigned_technicians?.[0]?.full_name
                const place = jobPlace(job)
                const extra = job.note?.trim() || null
                const showPlan = job.status === JOB_STATUS.completed && !!extra
                return (
                  <button
                    key={job.id}
                    type='button'
                    className='group relative flex min-h-12 w-full items-center gap-4 rounded-lg px-3 py-2 text-left hover:bg-[#f4f5f8]'
                    onClick={() => onOpenJob(job.id)}
                  >
                    <span
                      className='absolute inset-y-2 left-0 hidden w-1 rounded-r bg-[#ff5a1f] group-hover:block'
                      aria-hidden
                    />
                    <TimelineMarker
                      status={job.status}
                      last={i === items.length - 1}
                    />
                    <div className='flex min-w-0 flex-1 flex-col gap-1'>
                      <div className='flex flex-wrap items-center gap-3'>
                        <p className='text-sm font-medium leading-[1.4] text-[#171a20] group-hover:text-[#ff5a1f]'>
                          {job.service_type?.trim() ||
                            job.reference ||
                            t('detail.tabs.interventions')}
                        </p>
                        <JobStatusBadge
                          status={job.status}
                          label={t(`enums:jobStatus.${job.status}`, {
                            defaultValue: job.status.replace(/_/g, ' '),
                          })}
                          className='h-6 capitalize'
                        />
                      </div>
                      <div className='flex flex-wrap items-center gap-x-2 gap-y-1 text-xs font-normal leading-4 text-[#3d434e]'>
                        <span>{formatStamp(job, i18n.language)}</span>
                        {tech && (
                          <>
                            <MetaDot />
                            <span>{tech}</span>
                          </>
                        )}
                        {place && (
                          <>
                            <MetaDot />
                            <span>{place}</span>
                          </>
                        )}
                        {extra && (
                          <>
                            <MetaDot />
                            <span>{extra}</span>
                          </>
                        )}
                        {showPlan && (
                          <span className='inline-flex items-center gap-2 p-0.5 text-xs font-medium leading-none text-[#ff5a1f] group-hover:underline'>
                            {t('detail.recentInterventions.viewPlan')}
                            <ArrowRight className='size-4' />
                          </span>
                        )}
                      </div>
                    </div>
                  </button>
                )
              })
            )}
          </div>
        </section>

        <section
          className={cn(
            cardClass,
            'w-full xl:w-[min(60%,620px)] xl:shrink-0 [&_h2]:text-sm'
          )}
        >
          <CardHeader
            icon={
              <img
                src={paperclipIcon}
                alt=''
                width={15}
                height={16}
                className='h-[15.74px] w-[14.96px] max-w-none'
              />
            }
            iconBoxClass='bg-[#fbf3d9]'
            title={t('detail.attachments.title')}
          />
          <div className='px-6 pb-6 pt-4'>
            {documentsLoading ? (
              <p className='text-sm text-[#3d434e]'>{t('detail.loading')}</p>
            ) : (
              <ContractAttachments
                contractId={contract.id}
                clientId={contract.client_id}
                documents={documents}
                onDocumentsChanged={onDocumentsChanged}
                readOnly={!canEdit('contracts')}
                hideHeading
                hideUploadLabel
                brandTabs
                openPreviewOnClick
              />
            )}
          </div>
        </section>
      </div>

      <div className='flex flex-col gap-4 lg:flex-row lg:items-stretch'>
        <section
          className={cn(cardClass, cardH340, 'min-w-0 flex-1 [&_h2]:text-sm')}
        >
          <CardHeader
            icon={
              <img
                src={bugIcon}
                alt=''
                width={15}
                height={15}
                className='size-[15px] max-w-none'
              />
            }
            iconBoxClass='bg-[#ebf1fe]'
            title={t('detail.information.pestsTitle')}
          />
          <div className='flex flex-1 flex-wrap content-start gap-2 overflow-y-auto px-6 pb-6 pt-4'>
            {pests.length === 0 ? (
              <p className='text-sm text-[#3d434e]'>
                {t('detail.information.noServiceCategories')}
              </p>
            ) : (
              pests.map(cat => (
                <span
                  key={cat.id}
                  className='inline-flex items-center gap-2 rounded-lg border border-[#e7e9ef] bg-[#f4f5f8] px-3 py-2'
                >
                  <span className='flex size-4 shrink-0 items-center justify-center'>
                    <ServiceCategoryIcon name={cat.name} />
                  </span>
                  <span className='text-xs font-normal leading-4 text-[#171a20]'>
                    {cat.name}
                  </span>
                </span>
              ))
            )}
          </div>
        </section>

        <section
          className={cn(cardClass, cardH340, 'min-w-0 flex-1 [&_h2]:text-sm')}
        >
          <CardHeader
            icon={
              <img
                src={calendarPlanningIcon}
                alt=''
                width={14}
                height={15}
                className='h-[15px] w-3.5 max-w-none'
              />
            }
            iconBoxClass='bg-[#fbf3d9]'
            title={t('detail.information.planningTitle')}
          />
          <div className='flex flex-1 flex-col overflow-y-auto px-2 pb-4 pt-2'>
            {planningRows.map((row, i) => (
              <div
                key={row.label}
                className={cn(
                  'flex items-center justify-between gap-3 rounded-lg px-4 py-3',
                  i % 2 === 0 ? 'bg-[#f4f5f8]' : 'bg-white'
                )}
              >
                <span className='text-sm font-medium leading-[1.4] text-[#3d434e]'>
                  {row.label}
                </span>
                <span className='text-right text-sm font-medium leading-[1.4] text-[#171a20]'>
                  {row.value}
                </span>
              </div>
            ))}
          </div>
        </section>

        <section
          className={cn(cardClass, cardH340, 'min-w-0 flex-1 [&_h2]:text-sm')}
        >
          <CardHeader
            icon={
              <img
                src={wrenchIcon}
                alt=''
                width={15}
                height={15}
                className='size-[15px] max-w-none'
              />
            }
            iconBoxClass='bg-[#fff1ea]'
            title={t('detail.information.itemsToBringTitle')}
          />
          <div className='flex flex-1 flex-col overflow-y-auto px-6 pb-6 pt-2'>
            {bringItems.length === 0 ? (
              <p className='text-sm text-[#3d434e]'>
                {t('detail.information.noItemsToBring')}
              </p>
            ) : (
              bringItems.map((it, i) => (
                <BringItemRow
                  key={`${it.service_item_id ?? it.name}-${i}`}
                  item={it}
                  last={i === bringItems.length - 1}
                />
              ))
            )}
          </div>
        </section>
      </div>
    </div>
  )
}

function BringItemRow({
  item,
  last,
}: {
  item: ContractBringItem
  last: boolean
}) {
  return (
    <div
      className={cn(
        'flex items-center gap-3 py-3',
        !last && 'border-b border-dashed border-[#e7e9ef]'
      )}
    >
      <span className='flex size-8 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-[#f4f5f8]'>
        {item.image_url ? (
          <img src={item.image_url} alt='' className='size-8 object-cover' />
        ) : (
          <img
            src={wrenchIcon}
            alt=''
            width={15}
            height={15}
            className='size-[15px] max-w-none opacity-60'
          />
        )}
      </span>
      <p className='min-w-0 flex-1 truncate text-sm font-medium leading-[1.4] text-[#171a20]'>
        {item.name}
      </p>
      <div className='flex shrink-0 items-center gap-2'>
        {item.unit ? (
          <span className='text-sm leading-[1.4] text-[#3d434e]'>
            {item.unit}
          </span>
        ) : null}
        <span className='rounded-lg bg-[#fff1ea] px-1.5 py-0.5 text-xs font-medium leading-4 text-[#ff5a1f]'>
          x{item.quantity}
        </span>
      </div>
    </div>
  )
}
